import Button from 'components/Button/Button'
import CheckBox from 'components/CheckBox/CheckBox'
import Divider from 'components/Divider'
import { Input } from 'components/Input'
import Modal from 'components/Modal'
import RadioButton from 'components/RadioButton/RadioButton'
import Select from 'components/Select'
import { Text } from 'components/Text'
import { TransactionInvoice } from 'models/transaction.model'
import BillingInformation from 'pages/ClientProfile/BillingInformation'
import { CardInfo } from 'pages/ClientProfile/BillingInformation/BillingInformation.props'
import TransactionBalanceModal from 'pages/ClientProfile/BillingInformation/components/TransactionBalanceModal'
import React, { useEffect, useState } from 'react'
import { Col, Row } from 'react-grid-system'
import { useAppDispatch, useAppState } from 'store'
import { transactionActions } from 'store/slices/transactions/transactions.slice'
import {
  AddPaymentModalProps,
  PaymentTypeOption,
} from './AddPaymentModal.props'
import {
  AddPaymentFormGroup,
  AddPaymentFormRow,
} from './AddPaymentModal.styled'
import TransactionBalanceTable from './Components/TransactionBalanceTable'
import toast from 'cogo-toast'
import PageLoader from 'components/PageLoader/PageLoader'
import Spinner from 'components/Spinner'
import { PENNIES } from '../../../../utils/rate';
import { userActions } from '../../../../store/slices/users/users.slice';

type PaymentMethods =
  | 'cash'
  | 'check'
  | 'saved_credit_card'

const AddPaymentModal: React.FC<AddPaymentModalProps> = ({
  onClose,
  clientId,
  locationId,
}) => {

  const dispatch = useAppDispatch()

  const options: PaymentTypeOption[] = [
    { value: 'appointment', label: 'Appointment' },
    { value: 'recurringAppointment', label: 'Contract/Recurring Appointments' },
  ]

  const [
    selectedPaymentType,
    setSelectedPaymentType,
  ] = useState<PaymentTypeOption>(options[0])

  const [cardInfo, setCardInfo] = useState<CardInfo>({
    cardNumber: '',
    cardExpiry: '',
    cardCvv: '',
    cardName: '',
  })

  const [payment, setPayment] = useState<PaymentMethods>('cash');
  const [finalAmount, setFinalAmount] = useState<number>(0);
  const [partialPayment, setPartialPayment] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [selectedInvoices, setSelectedInvoices] = useState<
    TransactionInvoice[]
  >([])

  const handleSelectedPaymentType = (
    selectedPaymentType: PaymentTypeOption
  ) => {
    setSelectedInvoices([])
    setSelectedPaymentType(selectedPaymentType)
  }

  useEffect(() => {
    // Calculate finalAmount
    let finalAmount = 0
    for (const invoice of selectedInvoices) {
      finalAmount += invoice.amount / PENNIES
    }
    setFinalAmount(finalAmount)
  }, [selectedInvoices])

  const handlePayment = async () => {
    const invoices: number[] = []
    let amount = 0
    let tax = 0
    let subtotal = 0

    selectedInvoices.forEach(invoice => {
      amount = amount + invoice.amount
      tax = tax + invoice.tax
      subtotal = subtotal + invoice.subtotal
      invoices.push(invoice.id)
    })

    setLoading(true);

    const totalAmount = (partialPayment ? finalAmount * PENNIES : amount);

    await dispatch(
      transactionActions.createPaymentTransaction({
        locationId,
        clientId,
        amount: totalAmount,
        tax,
        subtotal: subtotal,
        invoice_ids: invoices,
        payment_type: payment,
      })
    ).then((res: any) => {

      setLoading(false);

      if (res.payload.error == false) {
        dispatch(
          userActions.getUserTransactionsByLocation({
            locationId,
            userId: clientId,
          })
        )
        toast.success('Payment created successfully', { position: 'top-right' })
      }

      onClose()
    })
  }

  return (
    <Modal
      showClose
      onClose={onClose}
      title="Pay for transactions"
      containerStyle={{ minHeight: 750 }}>
      {loading ? (
        <div style={{ height: '500px' }}>
          <Spinner />
        </div>
      ) : (
        <>
          <Row style={{ marginTop: '20px' }}>
            <Col sm={5}>
              <Select
                required
                label="Payment Type"
                options={options}
                value={selectedPaymentType}
                defaultValue={selectedPaymentType}
                onChange={paymentType =>
                  handleSelectedPaymentType(paymentType as PaymentTypeOption)
                }></Select>
            </Col>
            <Col sm={12}>
              <TransactionBalanceTable
                clientId={clientId}
                locationId={locationId}
                paymentType={selectedPaymentType.value}
                onSelectItem={items => {
                  setSelectedInvoices(items)
                }}
                pennies
              />
            </Col>
          </Row>

          <Row>
            <Col sm={12}>
              <Divider style={{ margin: '15px 0' }} />
            </Col>
          </Row>

          <AddPaymentFormRow>
            <Col xs={12} sm={12} md={4} lg={4}>
              <Row>
                <Col xs={12} sm={12} md={12} lg={12}>
                  <CheckBox
                    text="Partial Payment"
                    checked={partialPayment}
                    onChange={() => setPartialPayment(!partialPayment)}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={12} sm={12} md={8} lg={8}>
                  <Input
                    label="Total"
                    currencyMask
                    value={finalAmount}
                    disabled={!partialPayment}
                    onTextChange={finalAmount => setFinalAmount(+finalAmount)}
                  />
                </Col>
              </Row>
            </Col>
            <Col xs={12} sm={12} md={7} lg={7}>
              <Text text="Payment Method" weight="600" />

              <AddPaymentFormGroup>
                <RadioButton
                  key="radio-payment-1"
                  weight="500"
                  name="payment"
                  text={["Cash"]}
                  value="cash"
                  onChange={value =>
                    value && setPayment(value as PaymentMethods)
                  }
                  checked={payment === 'cash'}
                />
              </AddPaymentFormGroup>

              <AddPaymentFormGroup>
                <RadioButton
                  key="radio-payment-2"
                  weight="500"
                  name="payment"
                  text={["Check"]}
                  value="check"
                  onChange={value =>
                    value && setPayment(value as PaymentMethods)
                  }
                  checked={payment === 'check'}
                />
              </AddPaymentFormGroup>

              <AddPaymentFormGroup>
                <RadioButton
                  key="radio-payment-3"
                  weight="500"
                  name="payment"
                  text={["Credit Card"]}
                  value="saved_credit_card"
                  onChange={value =>
                    value && setPayment(value as PaymentMethods)
                  }
                  checked={payment === 'saved_credit_card'}
                />
              </AddPaymentFormGroup>

              {/*
                      payment === 'saved_saved_credit_card' && (
                        <BillingInformation cardInfo={cardInfo}/>
                      )
                      */}

              <Button
                text="SUBMIT"
                size="medium"
                variant="secondary"
                style={{ marginTop: '20px' }}
                disabled={!selectedInvoices.length}
                onClick={handlePayment}
              />
            </Col>
          </AddPaymentFormRow>
        </>
      )}
    </Modal>
  )
}

export default AddPaymentModal
